import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ConsumptionUploadEnhanced } from '../../consumption/components/ConsumptionUploadEnhanced';
import { BillUploadEnhanced } from '../../bill/components/BillUploadEnhanced';
import { PriceUploadEnhanced } from '../../price/components/PriceUploadEnhanced';
import { SolarUpload } from '../../solar/components/SolarUpload';
import { DataAlignment } from '../components/DataAlignmentFixed';
import { ProjectSelector } from '../components/ProjectSelector';
import { useCurrentProject, useProjectData, useProjectCompleteness } from '../../../hooks/useProjectData';
import { useBESSStore } from '../../../app/store';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../shared/components/ui';
import { CheckCircle, FileText, Receipt, DollarSign, AlignCenter, Sun } from 'lucide-react';
import { clsx } from 'clsx';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../services/api/client';

export function DataInputPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Solar state from store
  const {
    solarConfig,
    solarGeneration,
    setSolarEnabled,
    setSolarGeneration
  } = useBESSStore();

  // Get current project data using React Query
  const { data: currentProject, isLoading: projectLoading } = useCurrentProject();

  // Fetch existing solar data from backend when project loads
  const { data: solarSummary } = useQuery({
    queryKey: ['solarSummary', currentProject?.id],
    queryFn: async () => {
      if (!currentProject?.id) return null;
      const response = await apiClient.get('/solar/summary');
      return response.data;
    },
    enabled: !!currentProject?.id,
    staleTime: 0,
  });

  // Load existing solar data + apply user's saved preference when the project loads.
  // Keyed by project id so it runs once per project switch, not on every toggle click —
  // otherwise setSolarEnabled(false) would retrigger this effect and flip the toggle back on.
  useEffect(() => {
    if (!currentProject?.id) return;
    if (solarSummary?.success && solarSummary?.data && !solarGeneration) {
      setSolarGeneration(solarSummary.data);
    }
    if (typeof currentProject.solar_enabled === 'boolean') {
      // User has an explicit preference stored for this project — honor it.
      setSolarEnabled(currentProject.solar_enabled);
    } else if (solarSummary?.data) {
      // No stored preference; auto-detect: enable if data exists.
      setSolarEnabled(true);
    }
    // Intentionally excludes `solarConfig.solarEnabled` from deps: including it
    // creates a loop where the user's toggle triggers this effect and reverts it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject?.id, currentProject?.solar_enabled, solarSummary]);

  // Persist the toggle preference to the backend so it survives reloads.
  const handleSolarToggle = async (enabled: boolean) => {
    setSolarEnabled(enabled);
    if (!currentProject?.id) return;
    try {
      await apiClient.put(`/projects/${currentProject.id}`, { solar_enabled: enabled });
      queryClient.invalidateQueries(['current-project']);
      queryClient.invalidateQueries(['projects']);
    } catch (error) {
      console.error('[Solar] Failed to persist solar preference:', error);
    }
  };
  const { data: projectData } = useProjectData(currentProject?.id);

  // Load both original and aligned data at page level
  const { data: originalProjectData, refetch: refetchOriginal } = useProjectData(currentProject?.id, false);
  const { data: alignedProjectData, refetch: refetchAligned } = useProjectData(currentProject?.id, true);

  const { hasConsumption, hasBill, hasPrices, hasSelectedBarra, hasAllData, hasAlignedData } = useProjectCompleteness(currentProject?.id);

  // Solar data check
  const hasSolarData = solarConfig.solarEnabled ? !!solarGeneration : true;
  
  // Development mode: check for query param to skip to specific step
  const isDevelopment = import.meta.env.DEV;
  const urlParams = new URLSearchParams(window.location.search);
  const skipToStep = isDevelopment ? urlParams.get('step') : null;
  const initialStep = skipToStep ? parseInt(skipToStep) : 1;
  
  const [activeStep, setActiveStep] = useState(initialStep);
  const [isAlignmentValid, setIsAlignmentValid] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Scroll to top when active step changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeStep]);

  // Step 4 is completed when alignment is valid (both consumption and price are aligned)
  const isDataAligned = isAlignmentValid;

  const handleProceed = () => {
    if (isDevelopment && skipToStep === '4') {
      // In development mode with step=4, go directly to configuration
      navigate('/optimization/configure');
    } else if (isAlignmentValid) {
      // If data is aligned and valid, proceed
      navigate('/optimization');
    }
  };

  // Step 1 is complete when consumption is loaded AND (solar is disabled OR solar data is loaded)
  const isStep1Complete = hasConsumption && hasSolarData;

  const steps = [
    {
      id: 1,
      title: solarConfig.solarEnabled
        ? t('dataInput.steps.consumptionSolar.title', 'Consumo y Solar')
        : t('dataInput.steps.consumption.title'),
      description: solarConfig.solarEnabled
        ? t('dataInput.steps.consumptionSolar.description', 'Carga tus datos de consumo y generación solar')
        : t('dataInput.steps.consumption.description'),
      icon: solarConfig.solarEnabled ? Sun : FileText,
      completed: isStep1Complete,
      component: ConsumptionUploadEnhanced,
    },
    {
      id: 2,
      title: t('dataInput.steps.bill.title'),
      description: t('dataInput.steps.bill.description'),
      icon: Receipt,
      completed: hasBill,
      component: BillUploadEnhanced,
    },
    {
      id: 3,
      title: t('dataInput.steps.spotPrices.title'),
      description: t('dataInput.steps.spotPrices.description'),
      icon: DollarSign,
      completed: hasPrices && hasSelectedBarra,
      component: PriceUploadEnhanced,
    },
    {
      id: 4,
      title: t('dataInput.steps.alignment.title'),
      description: t('dataInput.steps.alignment.description'),
      icon: AlignCenter,
      completed: isDataAligned,
      component: null, // Will be rendered separately with props
    },
  ];

  const currentStep = steps[activeStep - 1];
  const CurrentComponent = currentStep.component;

  return (
    <div className="space-y-8">
      {/* Development Mode Notice */}
      {isDevelopment && skipToStep && (
        <div className="mx-auto max-w-6xl p-2 bg-yellow-100 border border-yellow-300 rounded text-sm text-yellow-800 text-center">
          {t('dataInput.development.notice', { step: skipToStep })}
        </div>
      )}
      
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-swiss-charcoal mb-2">
          {t('dataInput.header.title')}
        </h2>
        <p className="text-swiss-stone">
          {t('dataInput.header.subtitle')}
        </p>
      </div>

      {/* Project Selector */}
      <div className="max-w-md mx-auto mb-8">
        <ProjectSelector />
      </div>

      {/* Solar Toggle Card */}
      <div className="max-w-2xl mx-auto mb-8">
        <div className="bg-white border border-swiss-mist rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Sun className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-swiss-charcoal">
                  {t('dataInput.solar.toggle.title', '¿El proyecto incluye generación solar?')}
                </h4>
                <p className="text-sm text-swiss-stone">
                  {t('dataInput.solar.toggle.description', 'Habilita para cargar datos de generación fotovoltaica')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="solar-toggle"
                  checked={!solarConfig.solarEnabled}
                  onChange={() => handleSolarToggle(false)}
                  className="w-4 h-4 text-brand-primary"
                />
                <span className="text-sm text-swiss-charcoal">
                  {t('dataInput.solar.toggle.no', 'No, solo BESS')}
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="solar-toggle"
                  checked={solarConfig.solarEnabled}
                  onChange={() => handleSolarToggle(true)}
                  className="w-4 h-4 text-brand-primary"
                />
                <span className="text-sm text-swiss-charcoal">
                  {t('dataInput.solar.toggle.yes', 'Sí, tengo datos solares')}
                </span>
              </label>
            </div>
          </div>
          {solarConfig.solarEnabled && (
            <div className="mt-3 pt-3 border-t border-swiss-mist flex items-center gap-2">
              {solarGeneration ? (
                <>
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-700">
                    {t('dataInput.solar.toggle.loaded', 'Datos solares cargados')}
                    {' - '}
                    {solarGeneration.totalRecords.toLocaleString()} {t('dataInput.solar.toggle.records', 'registros')}
                  </span>
                </>
              ) : (
                <>
                  <Sun className="w-4 h-4 text-swiss-stone" />
                  <span className="text-sm text-swiss-stone">
                    {t('dataInput.solar.toggle.pending', 'Pendiente: cargar archivo de generación solar en el Paso 1')}
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Step Progress - Original minimalist design */}
      <div className="flex justify-center mb-8">
        <div className="flex items-center space-x-4">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <button
                onClick={() => setActiveStep(step.id)}
                className={clsx(
                  'flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors',
                  activeStep === step.id
                    ? 'bg-brand-primary text-white'
                    : step.completed
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-swiss-pearl text-swiss-stone hover:bg-swiss-mist'
                )}
              >
                <step.icon className="w-4 h-4" />
                <span className="font-medium">{step.title}</span>
                {step.completed && <CheckCircle className="w-4 h-4 ml-1" />}
              </button>
              {index < steps.length - 1 && (
                <div className={clsx(
                  'w-12 h-0.5 mx-2',
                  step.completed ? 'bg-green-500' : 'bg-swiss-mist'
                )} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Current Step Content */}
      <div className="max-w-6xl mx-auto">
        <div className="mb-4">
          <h3 className="text-xl font-semibold text-swiss-charcoal mb-1">
            {t('dataInput.currentStep.label', { step: currentStep.id, title: currentStep.title })}
          </h3>
          <p className="text-sm text-swiss-stone">{currentStep.description}</p>
        </div>
        
        {/* Render all components but only show the active one */}
        {currentStep.id === 1 && (
          <div className="space-y-6">
            <ConsumptionUploadEnhanced projectId={currentProject?.id} />
            {/* Show Solar Upload when solar is enabled */}
            {solarConfig.solarEnabled && (
              <SolarUpload />
            )}
          </div>
        )}
        {currentStep.id === 2 && (
          <BillUploadEnhanced projectId={currentProject?.id} />
        )}
        {currentStep.id === 3 && (
          <PriceUploadEnhanced projectId={currentProject?.id} />
        )}
        {/* Always render DataAlignment but only show when active - needed for validation */}
        <div style={{ display: currentStep.id === 4 ? 'block' : 'none' }}>
          <DataAlignment 
            key={refreshKey}
            onValidationChange={setIsAlignmentValid}
            projectId={currentProject?.id}
            originalData={originalProjectData}
            alignedData={alignedProjectData}
            onDataAligned={async () => {
              // Refetch both original and aligned data after alignment
              await Promise.all([refetchOriginal(), refetchAligned()]);

              // Invalidate and refetch all alignment-related caches immediately
              await queryClient.invalidateQueries({
                queryKey: ['patternMetrics'],
                refetchType: 'active'
              });
              await queryClient.invalidateQueries({
                queryKey: ['alignmentComparison'],
                refetchType: 'active'
              });
              await queryClient.invalidateQueries({
                queryKey: ['demandStatsComparison'],
                refetchType: 'active'
              });

              // Force component to re-render with new data
              setRefreshKey(prev => prev + 1);
            }}
          />
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          <Button
            variant="secondary"
            onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
            disabled={activeStep === 1}
          >
            {t('dataInput.navigation.previous')}
          </Button>

          {activeStep < 4 ? (
            <Button
              onClick={() => setActiveStep(Math.min(4, activeStep + 1))}
              disabled={!currentStep.completed}
            >
              {t('dataInput.navigation.next')}
            </Button>
          ) : (
            <Button
              onClick={handleProceed}
              disabled={!isAlignmentValid}
              variant="primary"
            >
              {t('dataInput.navigation.proceed')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}